import { useEffect, useState } from "react";
import { useMatch, useLocation, useNavigate } from "react-router-dom";

// formik components
import { Formik, Form } from "formik";

// Auth0
import { useAuth0 } from "@auth0/auth0-react";

// PropTypes
import PropTypes from "prop-types";

// Global controller
import { useSoftUIController } from "hooks/context";

// @mui material components
import { Grid, Card, Stepper, Step, StepLabel, Box, MobileStepper } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftTypography from "components/SoftComponents/SoftTypography";

import Swal from "sweetalert2";

// NewUser layout schemas for form and form feilds
import form from "./schemas/form";
import validationSchema from "./schemas/validationSchema";
import defaultServiceValues from "./schemas/initialValues";

import DeviceSelection from "./wizards/DeviceSelection";
import TechnicianActions from "./wizards/TechnicianActions";
import AdditionalNotes from "./wizards/AdditionalNotes";
import Review from "./wizards/Review";
import DeviceConditions from "./wizards/DeviceConditions";

function getStepContent(stepIndex, formData, deviceLayout) {
    switch (stepIndex) {
        case 0:
            return <DeviceSelection formData={formData} />
        case 1:
            return <DeviceConditions formData={formData} deviceLayout={deviceLayout} />;
        case 2:
            return <TechnicianActions formData={formData} deviceLayout={deviceLayout} />;
        case 3:
            return <AdditionalNotes formData={formData} />;
        case 4:
            return <Review formData={formData} />;
        default:
            return null;
    }
}

// function getSteps(formType) {
//     if (typeof formType != "string") {
//         switch (formType) {
//             case 0: // General Inspection
//                 return ["Location", "Inspection Type", "Device Search", "Device Notes", "Photo Upload", "Review"];
//             case 1: // Installation
//                 return ["Location", "Inspection Type", "Device Search", "Photo Upload", "Review"];
//             case 2: // Decommission
//                 return ["Location", "Inspection Type", "Device Search", "Photo Upload", "Review"];
//             case 3: // Replacement
//                 return ["Location", "Inspection Type", "Device Search", "Device Notes", "Replacement Device Search", "Photo Upload", "Review"];
//             default:
//                 return ["Unknown Form"];
//         }
//     }
//     else {
//         return ["Location", "Inspection Type"];
//     }
// }

function determineInspectionStatus(inspection_data) {
    var status = "resolved";

    for (var device of inspection_data.devices) {
        var conditions = Object.entries(device.conditions);
        var actions = Object.entries(device.actions);

        if (device.conditions.other.length > 0 || device.actions.other.length > 0) {
            return "review";
        }

        if (!(conditions.flat().find((el) => el === true) || actions.flat().find((el) => el === true))) {
            return "inspected";
        }

        for (var condition of conditions) {
            if (condition[1] && condition[0] != 'other') { // value of obj
                status = "unresolved";
                break;
            }
        }

        if (status == "unresolved") {
            var trimmed_actions = actions.map((row) => {
                return row[1] === true ? row[0] : null
            }).filter((row) => row != null);

            if (!trimmed_actions.length) {
                return status;
            }

            var actions_passed = Array(trimmed_actions.length).fill(false);

            console.log(trimmed_actions);

            for (var aIndex in trimmed_actions) {
                switch (trimmed_actions[aIndex]) {
                    case 'removed': // need to finish second device part of form
                    case 'installed':
                        break;
                    case 'cleaned':
                        actions_passed[aIndex] = true;
                        break;
                    case 'battery_swap':
                        if (device.conditions.power == true && trimmed_actions.filter((row) => row == 'battery_swap').length == 1) {
                            actions_passed[aIndex] = true;
                        }
                        break;
                    case 'hardware_changed':
                        if (device.conditions.audio == true && trimmed_actions.filter((row) => row == 'hardware_changed').length == 1) {
                            actions_passed[aIndex] = true;
                        }
                        break;
                    case 'cosmetically_fixed':
                        if ((device.conditions.vandalised == true || device.conditions.damaged == true) && trimmed_actions.filter((row) => row == 'cosmetically_fixed').length == 1) {
                            actions_passed[aIndex] = true;
                        }
                        break;
                    case 'other':
                        break;
                }
            }

            console.log(actions_passed)
            console.log(actions_passed.filter((row) => row === false))

            if (actions_passed.filter((row) => row === false).length == 0) {
                status = "resolved";
            }
        }
    }

    return status;
}

function InspectionsForm({ selected_user, all_devices, selected_device, onSubmit }) {
    const navigate = useNavigate();
    const { user } = useAuth0();
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const [deviceOptions, setDeviceOptions] = useState([]);
    const [formType, setFormType] = useState("");
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Device Selection", "Conditions", "Maintenance/Repairs", "Notes & Images", "Review"];//getSteps(formType);

    const { formId, formField } = form;
    const currentValidation = validationSchema[activeStep] || Yup.object().shape({});//[(formType != '' && activeStep) ? formType : 0];
    const isLastStep = activeStep === steps.length - 1;// && typeof formType != "string";

    const handleSubmit = (values, actions) => {
        // console.log(values, actions);
        if (isLastStep) {
            submitForm(values, actions);
        } else {
            setActiveStep(activeStep + 1);
            actions.setTouched({});
            actions.setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    function getObjValues(values, label) {
        var ret = {};
        var label_values = Object.keys(values).filter((row) => row.includes(label));

        for (var el of label_values) {
            var element_arr = el.split('-');
            var type = element_arr[(element_arr.length - 1)];
            ret[type] = values[`${label}-${type}`];
        }

        return ret;
    }

    const submitForm = async (values, actions) => {
        console.log(values, actions);
        // status can be "review", "inspected", "unresolved", "resolved"

        var formData = {
            metadata: {
                title: values.title == "" ? values.inspection_type : values.title,
                description: values.description,
                organisation_id: values.organisation_id,
                location: values.location,
                devices: [],
                technician: user.nickname.split('.')[0]
            }
        };

        for (var deviceIndex = 0; deviceIndex < 2; deviceIndex++) {
            var deviceLabel = deviceIndex == 0 ? 'devicePrimary' : 'deviceSecondary';

            if (values[`${deviceLabel}-info`] != "") {

                // debugger;
                values[`${deviceLabel}-images`].forEach((image, imageIndex) => {
                    formData[`device${deviceIndex}_image${imageIndex}`] = image.file;
                });

                formData.metadata.devices[deviceIndex] = {
                    uuid: values[`${deviceLabel}-uuid`],
                    name: values[`${deviceLabel}-info`].value.name,
                    serial_no: values[`${deviceLabel}-serial_no`],
                    stop_id: values[`${deviceLabel}-stop_id`],
                    conditions: getObjValues(values, `${deviceLabel}-conditions`),
                    actions: getObjValues(values, `${deviceLabel}-actions`),
                    images: values[`${deviceLabel}-images`].map((image, imageIndex) => {
                        return {
                            key: `device${deviceIndex}_image${imageIndex}`,
                            previewUrl: image.previewUrl
                        }
                    })
                }
            }
        }

        formData.metadata.status = determineInspectionStatus(formData.metadata);


        try {
            Swal.fire({
                icon: "warning",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Validating...</div>`,
                html: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif;">Processing inspection data and images...</div>',
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const result = await onSubmit(formData);
            console.log("Submission successful:", result);

            await Swal.fire({
                icon: "success",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                showConfirmButton: false,
                timer: 2000,
            });

            navigate(-1);

        } catch (err) {
            console.error("Submission failed:", err);
            const errorMessage = /*err.message || */"Something went wrong while saving.";

            actions.setSubmitting(false);

            await Swal.fire({
                icon: "error",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${errorMessage}</div>`,
                showConfirmButton: true,
                timer: 15000,
            });
        }
    }

    return (
        <SoftBox px={1} pb={1} py={deviceLayout == "mobile" ? 1 : "1rem"}>
            {deviceLayout == "mobile" ? (
                <>
                    {/* <SoftBox mb={3}
                        sx={({ palette: { white }, functions: { rgba } }) => ({
                            borderRadius: "15px",
                            height: '80px',
                            backgroundColor: rgba(white.main, 0.5),
                            flexWrap: 0,
                            alignContent: 'center',
                            justifyContent: "center",
                        })}
                    >
                        <MobileStepper
                            variant="dots"
                            steps={steps.length}
                            activeStep={activeStep}
                            sx={({ palette: { white }, functions: { rgba } }) => ({
                                borderRadius: "15px",
                                position: 'unset',
                                height: '15px',
                                backgroundColor: rgba(white.main, 0),
                                // maxWidth: 400,
                                flexWrap: 0,
                                alignContent: 'center',
                                justifyContent: "center",

                                "& .MuiMobileStepper-dots .MuiMobileStepper-dot": {
                                    width: '12px',
                                    height: '12px',
                                    margin: '0 4px'
                                },
                            })}
                        />
                        <SoftBox display="flex" justifyContent="center">
                            <SoftTypography
                                variant="body2"
                                fontWeight="bold"
                                textTransform="uppercase"
                            >
                                {steps[activeStep]}
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox> */}
                </>
            ) : (
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}
            <Formik
                initialValues={defaultServiceValues}
                validationSchema={currentValidation}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}>
                {({ values, errors, touched, isSubmitting, setFieldValue, handleChange }) => {
                    Object.keys(errors).length > 0 && console.log("Validation Errors:", errors);

                    return (
                        <Form id={formId} autoComplete="off">
                            <Card sx={{ overflow: "visible", height: '100%' }}>
                                <SoftBox px={{xxs: 1, sm: 3}} py={{xxs: 2, sm: 3}} height="100%">
                                    {/* <SoftBox> */}
                                    {getStepContent(activeStep, {
                                        values,
                                        touched,
                                        errors,
                                        isSubmitting,
                                        formField,
                                        functions: {
                                            setDeviceOptions,
                                            setFormType,
                                            setFieldValue,
                                            handleChange,
                                            handleBack,
                                            handleCancel
                                        },
                                        custom: {
                                            activeStep,
                                            isLastStep,
                                            user: selected_user,
                                            selected_device,
                                            all_devices,
                                            deviceOptions
                                        }
                                    },
                                        deviceLayout
                                    )}
                                    {/* <SoftBox mt={5} width="100%" display="flex" justifyContent="space-between">
                                            <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                                                {activeStep === 0 ? "cancel" : "back"}
                                            </SoftButton>
                                            <SoftButton
                                                disabled={isSubmitting}
                                                type="submit"
                                                variant="gradient"
                                                color="dark"
                                            >
                                                {isLastStep ? "submit" : "next"}
                                            </SoftButton>
                                        </SoftBox>
                                    </SoftBox> */}
                                </SoftBox>
                            </Card>
                        </Form>
                    )
                }}
            </Formik>
        </SoftBox>
    );
}

InspectionsForm.propTypes = {
    selected_user: PropTypes.object.isRequired,
    all_devices: PropTypes.array.isRequired,
    selected_device: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
    onSubmit: PropTypes.func,
};

export default InspectionsForm;